import {
  InvoiceClientContactInfo,
  InvoiceCompanyHeaderInfo,
} from "./bitmaker/schemas/Billing/Invoice";
import { CompanyInfo } from "./bitmaker/schemas/Billing/CompanyInfo";

export function exists(json: any, key: string) {
  const value = json[key];
  return value !== null && value !== undefined;
}

export function monthsRange(fromDate: Date, toDate: Date): Array<Date> {
  const months: Date[] = [];

  let currentDate = new Date(fromDate.getTime());
  const endDate = new Date(toDate.getTime());

  currentDate.setDate(1);
  endDate.setDate(1);

  while (currentDate <= endDate) {
    months.push(new Date(currentDate.getTime()));
    currentDate.setMonth(currentDate.getMonth() + 1);
  }

  return months;
}

export function hoursReadable(hours: number | undefined): string {
  if (hours === undefined) {
    return `0h`;
  }
  const minutes = Math.trunc((hours - Math.trunc(hours)) * 60);
  if (minutes > 0) {
    return `${Math.trunc(hours)}h ${minutes}m`;
  }
  return `${Math.trunc(hours)}h`;
}

export interface MonthRange {
  year: number;
  month: string;
  firstDay: string;
  lastDay: string;
}

export function getMonthRange(currentDate: Date): MonthRange {
  return {
    year: currentDate.getUTCFullYear(),
    month: ("0" + (currentDate.getUTCMonth() + 1)).slice(-2),
    firstDay: "01",
    lastDay: (
      "0" +
      new Date(
        currentDate.getUTCFullYear(),
        currentDate.getUTCMonth() + 1,
        0,
      ).getDate()
    ).slice(-2),
  };
}

export function fromDateToString(currentDate: Date | null | undefined): string {
  let date = "";
  if (currentDate != null) {
    let year = currentDate.getUTCFullYear();
    let month = ("0" + (currentDate.getUTCMonth() + 1)).slice(-2);
    let day = ("0" + currentDate.getUTCDate()).slice(-2);
    date = year + "-" + month + "-" + day;
  }

  return date;
}

export function fromDateToStringWithMonthInLetters(date: Date): string {
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const day = date.getUTCDate().toString().padStart(2, "0");
  const month = months[date.getUTCMonth()];
  const year = date.getUTCFullYear().toString().slice(-2);

  return `${day}-${month}-${year}`;
}

export function fromDateToStringWithFullFormat(date: Date): string {
  const weekdays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const weekday = weekdays[date.getUTCDay()];
  const day = date.getUTCDate().toString().padStart(2, "0");
  const month = months[date.getUTCMonth()];
  const year = date.getUTCFullYear();

  return `${weekday} ${month} ${day} ${year}`;
}

export function fromDatetimeToString(
  currentDate: Date | null | undefined,
): string {
  if (currentDate != null) {
    const year = currentDate.getFullYear();
    const month = ("0" + (currentDate.getMonth() + 1)).slice(-2);
    const day = ("0" + currentDate.getDate()).slice(-2);
    const hours = ("0" + currentDate.getHours()).slice(-2);
    const minutes = ("0" + currentDate.getMinutes()).slice(-2);
    const seconds = ("0" + currentDate.getSeconds()).slice(-2);

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  }
  return "";
}

export function formatDate(currentDate: Date | undefined): string {
  // Returns the month name in short format and the year (Jan 2023)
  let formatDate = "";
  if (currentDate !== undefined) {
    let year = currentDate.getUTCFullYear();
    let month = currentDate.getUTCMonth();
    let date = new Date(year, month);
    formatDate = date.toLocaleString("en-us", {
      month: "short",
      year: "numeric",
    });
  }

  return formatDate;
}

export function toUTCDateOnly(date: Date) {
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth();
  const day = date.getUTCDate();

  return new Date(year, month, day);
}

export function formatTime(time: string | undefined): number {
  let hours = 0;
  if (time === undefined) {
    return hours;
  }
  let hoursData = time.split("h");
  if (hoursData.length > 1) {
    hours = parseFloat(hoursData[0]);
  }
  let minutesData = hoursData.length > 1 ? hoursData[1] : hoursData[0];
  let minutes = minutesData.split("m");
  if (minutes[0]) {
    hours += parseFloat(minutes[0]) / 60;
  }

  return hours;
}

export function getLimitActivity(limits: any, limit: any, key: string): string {
  let activity = "total";
  if (limit[key].depends_on) {
    for (var limitObject of limits) {
      if (
        limitObject[limit[key].depends_on] &&
        limitObject[limit[key].depends_on].activity
      ) {
        activity = limitObject[limit[key].depends_on].activity;
      }
    }
  } else {
    if (limit[key].activity) {
      activity = limit[key].activity;
    }
  }

  return activity;
}

export function isDateGreaterThan(date1: Date, date2: Date): boolean {
  const strippedDate1 = new Date(
    date1.getUTCFullYear(),
    date1.getUTCMonth(),
    date1.getUTCDate(),
  );
  const strippedDate2 = new Date(
    date2.getUTCFullYear(),
    date2.getUTCMonth(),
    date2.getUTCDate(),
  );

  return strippedDate1.getTime() > strippedDate2.getTime();
}

export function compareDates(date1: string, date2: string) {
  if (!date1 || !date2) {
    return date1 ? -1 : date2 ? 1 : 0;
  }

  return new Date(date1).getTime() - new Date(date2).getTime();
}

export function generateSprintDates(
  startDate: Date | null | undefined,
  endDate: Date | null | undefined,
) {
  if (!startDate || !endDate) {
    return [];
  }

  const sprintDates = [];
  let currentDate = new Date(startDate);

  while (currentDate <= endDate) {
    // Exclude Saturdays (Saturday = 6) and Sundays (Sunday = 0)
    if (currentDate.getUTCDay() !== 6 && currentDate.getUTCDay() !== 0) {
      sprintDates.push(currentDate.toISOString().split("T")[0]); // Format as 'YYYY-MM-DD' in UTC
    }
    currentDate.setUTCDate(currentDate.getUTCDate() + 1);
  }

  return sprintDates;
}

export function generateFilters(
  items: Array<any>,
  extractItems: (item: any) => Array<string>,
): Array<{ text: string; value: string }> {
  const allItems = new Set<string>();

  items.forEach((item) => {
    const extractedItems = extractItems(item);
    if (extractedItems) {
      extractedItems.forEach((extractedItem) => {
        allItems.add(extractedItem);
      });
    }
  });

  return Array.from(allItems)
    .sort()
    .map((extractedItem) => ({
      text: extractedItem,
      value: extractedItem,
    }));
}

export const formatCurrency = (
  value: number | string | null | undefined,
  currency: "USD" | "PEN" = "USD",
  language: "en" | "es" = "en",
) => {
  const numericValue = Number(value ?? 0);

  const locale = language === "es" ? "es-PE" : "en-US";

  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numericValue);
};

export const formatClientInformation = (
  props: InvoiceClientContactInfo,
): string[] => {
  const lines: string[] = [];
  const {
    clientCorporateName,
    clientCompanyName,
    clientFirstName,
    clientLastName,
    clientAddressLine,
    clientTownCity,
    clientStateProvince,
    clientPostalCode,
    clientCountry,
  } = props;

  const fullName = [clientFirstName, clientLastName].filter(Boolean).join(" ");
  if (fullName) lines.push(fullName);

  const companyName = clientCorporateName || clientCompanyName;
  if (companyName) {
    lines.push(companyName);
  }

  if (clientAddressLine) {
    lines.push(clientAddressLine);
  }

  const cityStatePart = [clientTownCity, clientStateProvince]
    .filter(Boolean)
    .join(", ");
  const postalCode = clientPostalCode;
  let cityStateLine = "";

  if (cityStatePart && postalCode) {
    cityStateLine = `${cityStatePart} ${postalCode}`;
  } else if (!cityStatePart && postalCode) {
    cityStateLine = postalCode;
  } else {
    cityStateLine = cityStatePart;
  }

  if (cityStateLine) {
    lines.push(cityStateLine);
  }

  if (clientCountry) {
    lines.push(clientCountry);
  }

  return lines;
};

export const cleanValue = (value: string | null | undefined): string | null => {
  if (value === null || value === undefined) {
    return null;
  }

  const trimmedValue = String(value).trim();

  return trimmedValue === "" ? null : trimmedValue;
};

export const mapCompanyInfoToHeader = (
  currentInfo: CompanyInfo | null,
): InvoiceCompanyHeaderInfo => {
  return {
    companyCorporateName: currentInfo?.corporateName || null,
    companyName: currentInfo?.companyName || null,
    companyEmailAddress: currentInfo?.emailAddress || null,
    companyPhoneNumber: currentInfo?.phoneNumber || null,
    companyAddressLine: currentInfo?.addressLine || null,
    companyTownCity: currentInfo?.townCity || null,
    companyStateProvince: currentInfo?.stateProvince || null,
    companyPostalCode: currentInfo?.postalCode || null,
    companyCountry: currentInfo?.country || null,
    companyTaxName: currentInfo?.taxName || null,
    companyTaxNumber: currentInfo?.taxNumber || null,
  };
};
